import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import { Button, Dialog, Tabs, Tab, TextField, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { savePerformance, getPerformances, deletePerformance } from '../api/performance';
import dayjs from 'dayjs';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const months = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

function getDaysInMonth(month: number, year: number) {
  return new Date(year, month + 1, 0).getDate();
}

function Performances() {
  const theme = useTheme();

  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [year, setYear] = useState(dayjs().year());
  // Structure: { [key: string]: { day: number, caMidi: number, couvertsMidi: number, couvertsSoir: number, caJour: number }[] }
  const [monthlyData, setMonthlyData] = useState<{ [key: string]: any[] }>({});
  const [popupDay, setPopupDay] = useState<number|null>(null);
  const [popupValues, setPopupValues] = useState<any>({});
  const [confirmDeleteDay, setConfirmDeleteDay] = useState<number | null>(null);

  const handleOpenPopup = (day: number, row: any) => {
    setPopupDay(day);
    setPopupValues({
      caMidi: row.caMidi || '',
      couvertsMidi: row.couvertsMidi || '',
      couvertsSoir: row.couvertsSoir || '',
      caSoir: row.caSoir || '', // Ajout pour bien pré-remplir le champ CA Soir
      caJour: row.caJour || '',
    });
  };
  const handlePopupChange = (field: string, value: any) => {
    let parsedValue = value;
    if (typeof value === 'string') {
      parsedValue = parseFloat(value.replace(',', '.'));
      if (isNaN(parsedValue)) parsedValue = '';
    }
    setPopupValues((prev: any) => ({ ...prev, [field]: parsedValue }));
  };
  const handleSavePopup = async () => {
    if (popupDay) {
      try {
        const jsDay = new Date(year, selectedMonth, popupDay).getDay();
        const dayOfWeek = jsDay === 0 ? 6 : jsDay - 1;
        const caMidi = Number(popupValues.caMidi);
        const caJour = Number(popupValues.caJour);
        const caSoir = caJour - caMidi;
        const perf = {
          year,
          month: selectedMonth,
          day: popupDay,
          dayOfWeek,
          caMidi,
          couvertsMidi: Number(popupValues.couvertsMidi),
          caSoir,
          couvertsSoir: Number(popupValues.couvertsSoir),
          caJour,
          coutSalaire: Number(popupValues.coutSalaire),
          masseSalariale: Number(popupValues.masseSalariale),
        };
        await savePerformance(perf);
        const res = await getPerformances(year, selectedMonth);
        if (res && res.code === 200 && res.data) {
          setMonthlyData(prev => ({
            ...prev,
            [`${year}-${selectedMonth}`]: res.data
          }));
        }
      } catch (error) {
        console.error('Error saving performance:', error);
      }
    }
    setPopupDay(null);
  };

  useEffect(() => {
    const fetchMonthlyData = async () => {
      try {
        const res = await getPerformances(year, selectedMonth);
        if (res && res.code === 200 && res.data && res.data.length > 0) {
          setMonthlyData(prev => ({
            ...prev,
            [`${year}-${selectedMonth}`]: res.data
          }));
        }
      } catch (error) {
        console.error('Error fetching performances:', error);
      }
    };
    fetchMonthlyData();
  }, [year, selectedMonth]);

  // Fonction pour supprimer une performance et rafraîchir les données
  const handleDeleteRow = async (day: number) => {
    setConfirmDeleteDay(day); // Ouvre la popup de confirmation
  };

  const confirmDelete = async () => {
    if (confirmDeleteDay !== null) {
      try {
        await deletePerformance(year, selectedMonth, confirmDeleteDay);
        const res = await getPerformances(year, selectedMonth);
        if (res && res.code === 200 && res.data) {
          setMonthlyData(prev => ({
            ...prev,
            [`${year}-${selectedMonth}`]: res.data
          }));
        }
      } catch (error) {
        console.error('Error deleting performance:', error);
      }
      setConfirmDeleteDay(null);
    }
  };

  // Fonction pour exporter les données du mois en CSV
  const handleExportMonth = () => {
    const data = monthlyData[`${year}-${selectedMonth}`] || [];
    
    // En-têtes CSV
    const headers = [
      'Jour',
      'Date',
      'CA Midi (€)',
      'Couverts Midi',
      'Moy. CA/Couvert Midi (€)',
      'CA Soir (€)',
      'Couverts Soir',
      'Moy. CA/Couvert Soir (€)',
      'CA Jour (€)',
      'Couverts Jour',
      'Moy. CA/Couvert Jour (€)',
      'Coût Salaire',
      'Masse Salariale'
    ];
    
    // Générer les lignes
    const rows = Array.from({ length: getDaysInMonth(selectedMonth, year) }, (_, i) => i + 1).map(day => {
      const row = data.find((d: any) => d.day === day) || {};
      const couvertsJour = (row.couvertsMidi || 0) + (row.couvertsSoir || 0);
      const caSoir = (row.caJour || 0) - (row.caMidi || 0);
      const moyCaCouvertsMidi = row.couvertsMidi ? ((row.caMidi || 0) / row.couvertsMidi) : '';
      const moyCaCouvertsSoir = row.couvertsSoir ? ((row.caSoir || caSoir || 0) / row.couvertsSoir) : '';
      const moyCaCouvertsJour = couvertsJour ? ((row.caJour || 0) / couvertsJour) : '';
      
      const dateObj = new Date(year, selectedMonth, day);
      const daysShort = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
      const dayName = daysShort[dateObj.getDay()];
      const dateStr = `${dayName} ${String(day).padStart(2, '0')}/${String(selectedMonth+1).padStart(2, '0')}`;
      
      return [
        dayName,
        dateStr,
        row.caMidi || '',
        row.couvertsMidi || '',
        moyCaCouvertsMidi ? Number(moyCaCouvertsMidi).toFixed(2) : '',
        caSoir || '',
        row.couvertsSoir || '',
        moyCaCouvertsSoir ? Number(moyCaCouvertsSoir).toFixed(2) : '',
        row.caJour || '',
        couvertsJour || '',
        moyCaCouvertsJour ? Number(moyCaCouvertsJour).toFixed(2) : '',
        row.coutSalaire || '',
        row.masseSalariale || ''
      ];
    });
    
    // Créer le contenu CSV
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    // Créer et télécharger le fichier
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `performances_${months[selectedMonth]}_${year}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour exporter les données du mois en PDF
  const handleExportPDF = () => {
    const data = monthlyData[`${year}-${selectedMonth}`] || [];
    const doc = new jsPDF();
    
    // Titre
    doc.setFontSize(18);
    doc.text(`Performances - ${months[selectedMonth]} ${year}`, 14, 20);
    
    // Préparer les données pour le tableau
    const headers = [
      ['Jour', 'CA Midi', 'Couv. Midi', 'Moy. Midi', 'CA Soir', 'Couv. Soir', 'Moy. Soir', 'CA Jour', 'Couv. Jour', 'Moy. Jour']
    ];
    
    const rows = Array.from({ length: getDaysInMonth(selectedMonth, year) }, (_, i) => i + 1).map(day => {
      const row = data.find((d: any) => d.day === day) || {};
      const couvertsJour = (row.couvertsMidi || 0) + (row.couvertsSoir || 0);
      const caSoir = (row.caJour || 0) - (row.caMidi || 0);
      const moyCaMidi = row.couvertsMidi ? ((row.caMidi || 0) / row.couvertsMidi).toFixed(2) : '';
      const moyCaSoir = row.couvertsSoir ? ((row.caSoir || caSoir || 0) / row.couvertsSoir).toFixed(2) : '';
      const moyCaJour = couvertsJour ? ((row.caJour || 0) / couvertsJour).toFixed(2) : '';
      
      const dateObj = new Date(year, selectedMonth, day);
      const daysShort = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
      const dayName = daysShort[dateObj.getDay()];
      const dateStr = `${dayName} ${String(day).padStart(2, '0')}`;
      
      return [
        dateStr,
        row.caMidi ? `${Number(row.caMidi).toFixed(2)}€` : '',
        row.couvertsMidi || '',
        moyCaMidi ? `${moyCaMidi}€` : '',
        caSoir ? `${Number(caSoir).toFixed(2)}€` : '',
        row.couvertsSoir || '',
        moyCaSoir ? `${moyCaSoir}€` : '',
        row.caJour ? `${Number(row.caJour).toFixed(2)}€` : '',
        couvertsJour || '',
        moyCaJour ? `${moyCaJour}€` : ''
      ];
    });
    
    // Ajouter le tableau au PDF
    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 30,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [200, 160, 89], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 18 },
        2: { cellWidth: 15 },
        3: { cellWidth: 15 },
        4: { cellWidth: 18 },
        5: { cellWidth: 15 },
        6: { cellWidth: 15 },
        7: { cellWidth: 18 },
        8: { cellWidth: 15 },
        9: { cellWidth: 15 }
      }
    });
    
    // Calculer les totaux et moyennes
    const nbJours = data.filter((d: any) => d.caJour !== undefined && d.caJour !== '').length;
    const totalCAMidi = data.reduce((sum: number, d: any) => sum + (Number(d.caMidi) || 0), 0);
    const totalCASoir = data.reduce((sum: number, d: any) => sum + (Number(d.caSoir) || 0), 0);
    const totalCAJour = data.reduce((sum: number, d: any) => sum + (Number(d.caJour) || 0), 0);
    const totalCouvertsMidi = data.reduce((sum: number, d: any) => sum + (Number(d.couvertsMidi) || 0), 0);
    const totalCouvertsSoir = data.reduce((sum: number, d: any) => sum + (Number(d.couvertsSoir) || 0), 0);
    
    // Ajouter les résumés en bas
    const finalY = (doc as any).lastAutoTable.finalY || 30;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Résumé du mois :', 14, finalY + 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Jours ouvrés : ${nbJours}`, 14, finalY + 17);
    doc.text(`Total CA Midi : ${totalCAMidi.toFixed(2)}€`, 14, finalY + 24);
    doc.text(`Total CA Soir : ${totalCASoir.toFixed(2)}€`, 14, finalY + 31);
    doc.text(`Total CA Jour : ${totalCAJour.toFixed(2)}€`, 14, finalY + 38);
    doc.text(`Total Couverts Midi : ${totalCouvertsMidi}`, 100, finalY + 17);
    doc.text(`Total Couverts Soir : ${totalCouvertsSoir}`, 100, finalY + 24);
    doc.text(`Moyenne CA/jour : ${nbJours > 0 ? (totalCAJour / nbJours).toFixed(2) : 0}€`, 100, finalY + 31);
    
    // Sauvegarder le PDF
    doc.save(`performances_${months[selectedMonth]}_${year}.pdf`);
  };

  // Fonction pour afficher le tableau des performances avec DataGrid
  const renderTable = () => {
    const data = monthlyData[`${year}-${selectedMonth}`] || [];
    
    // Préparer les lignes pour le DataGrid
    const rows = Array.from({ length: getDaysInMonth(selectedMonth, year) }, (_, i) => i + 1).map(day => {
      const row = data.find((d: any) => d.day === day) || {};
      const couvertsJour = (row.couvertsMidi || 0) + (row.couvertsSoir || 0);
      const caSoir = (row.caJour || 0) - (row.caMidi || 0);
      const moyCaCouvertsMidi = row.couvertsMidi ? ((row.caMidi || 0) / row.couvertsMidi) : null;
      const moyCaCouvertsSoir = row.couvertsSoir ? ((row.caSoir || caSoir || 0) / row.couvertsSoir) : null;
      const moyCaCouvertsJour = couvertsJour ? ((row.caJour || 0) / couvertsJour) : null;
      
      const dateObj = new Date(year, selectedMonth, day);
      const daysShort = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
      const dayName = daysShort[dateObj.getDay()];
      const dateStr = `${dayName} ${String(day).padStart(2, '0')}/${String(selectedMonth+1).padStart(2, '0')}`;
      const isToday = day === dayjs().date() && selectedMonth === dayjs().month() && year === dayjs().year();
      
      return {
        id: day,
        day,
        dateStr,
        isToday,
        caMidi: row.caMidi,
        couvertsMidi: row.couvertsMidi,
        moyCaCouvertsMidi,
        caSoir,
        couvertsSoir: row.couvertsSoir,
        moyCaCouvertsSoir,
        caJour: row.caJour,
        couvertsJour,
        moyCaCouvertsJour,
        coutSalaire: row.coutSalaire,
        masseSalariale: row.masseSalariale,
        hasData: !!row.day
      };
    });

    // Définir les colonnes
    const columns: GridColDef[] = [
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <div className='flex flex-row gap-2 justify-center items-center h-full'>
            {params.row.hasData && (
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Supprimer"
                onClick={() => handleDeleteRow(params.row.day)}
                color="error"
              />
            )}
            <GridActionsCellItem
              icon={<EditIcon style={{ color: theme.palette.warning.main }} />}
              label="Modifier"
              onClick={() => handleOpenPopup(params.row.day, monthlyData[`${year}-${selectedMonth}`]?.find((d: any) => d.day === params.row.day) || {})}
            />
          </div>
        ),
      },
      {
        field: 'dateStr',
        headerName: 'Jour',
        width: 100,
        renderCell: (params) => (
          <span style={{ fontWeight: params.row.isToday ? 700 : 400 }}>{params.value}</span>
        ),
      },
      {
        field: 'caMidi',
        headerName: 'CA Midi',
        width: 100,
        type: 'number',
        valueFormatter: (value) => value != null && value !== '' ? `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '',
      },
      {
        field: 'couvertsMidi',
        headerName: 'Couverts Midi',
        width: 150,
        type: 'number',
      },
      {
        field: 'moyCaCouvertsMidi',
        headerName: 'Moy. Midi',
        width: 100,
        type: 'number',
        valueFormatter: (value) => value != null ? `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '',
      },
      {
        field: 'caSoir',
        headerName: 'CA Soir',
        width: 100,
        type: 'number',
        valueFormatter: (value) => value != null && value !== '' && value !== 0 ? `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '',
      },
      {
        field: 'couvertsSoir',
        headerName: 'Couverts Soir',
        width: 150,
        type: 'number',
      },
      {
        field: 'moyCaCouvertsSoir',
        headerName: 'Moy. Soir',
        width: 100,
        type: 'number',
        valueFormatter: (value) => value != null ? `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '',
      },
      {
        field: 'caJour',
        headerName: 'CA Jour',
        width: 100,
        type: 'number',
        valueFormatter: (value) => value != null && value !== '' ? `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '',
      },
      {
        field: 'couvertsJour',
        headerName: 'Couverts Jour',
        width: 150,
        type: 'number',
      },
      {
        field: 'moyCaCouvertsJour',
        headerName: 'Moy. Jour',
        width: 100,
        type: 'number',
        valueFormatter: (value) => value != null ? `${Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '',
      },
      {
        field: 'coutSalaire',
        headerName: 'Coût salaire',
        width: 100,
        type: 'number',
      },
      {
        field: 'masseSalariale',
        headerName: 'Masse salariale',
        width: 160,
        type: 'number',
      },
    ];

    return (
      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
          disableColumnResize
          initialState={{
            pagination: {
              paginationModel: { pageSize: 31, page: 0 },
            },
          }}
          pageSizeOptions={[31]}
          getRowClassName={(params) => params.row.isToday ? 'today-row' : ''}
          sx={{
            '& .today-row': {
              backgroundColor: `${theme.palette.primary.main}25`,
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      </div>
    );
  };

  return (
      <Paper elevation={0} style={{ flex: 1, padding: '10px' }}>
        <div className='flex flex-row justify-end items-center w-full'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button 
              variant="contained" 
              onClick={handleExportMonth}
              sx={{ 
                background: theme.palette.primary.main, 
                color: '#fff',
                '&:hover': { background: theme.palette.primary.dark } 
              }}
            >
              📊 Exporter CSV
            </Button>
            <Button 
              variant="contained" 
              onClick={handleExportPDF}
              sx={{ 
                background: theme.palette.secondary.main, 
                color: '#fff',
                '&:hover': { background: theme.palette.secondary.dark } 
              }}
            >
              📄 Exporter PDF
            </Button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: "background.paper",
              borderRadius: 24,
              boxShadow: 'none',
              padding: 0,
              minWidth: 0,
              justifyContent: 'center',
            }}>
              <Button 
                onClick={() => setYear(y => y - 1)} 
                size="small" 
                sx={{ minWidth: 0, borderRadius: '50%', background: "background.paper", boxShadow: 'none', p: 0, '&:hover': { background: "primary.main" } }}
              >
                <ArrowBackIosIcon fontSize="small" style={{ color: "primary.main" }} />
              </Button>
              <span style={{ fontSize: 22, fontWeight: 700, color: "primary.main", minWidth: 60, textAlign: 'center', letterSpacing: 1 }}>{year}</span>
              <Button 
                onClick={() => setYear(y => y + 1)} 
                size="small" 
                sx={{ minWidth: 0, borderRadius: '50%', background: "background.paper", boxShadow: 'none', p: 0, '&:hover': { background: "primary.main" } }}
              >
                <ArrowForwardIosIcon fontSize="small" style={{ color: "primary.main" }} />
              </Button>
            </div>
          </div>
        </div>
        <Tabs
          value={selectedMonth}
          onChange={(_, newValue) => setSelectedMonth(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {months.map((m, idx) => (
            <Tab key={m} label={m} />
          ))}
        </Tabs>
        {/* Cartes totaux et moyennes du mois */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, marginBottom: 32, marginTop: 20 }}>
          {(() => {
            const fields = [
              { key: 'caMidi', label: 'CA Midi (€)' },
              { key: 'caSoir', label: 'CA Soir (€)' },
              { key: 'caJour', label: 'CA Jour (€)' },
              { key: 'couvertsMidi', label: 'Couverts Midi' },
              { key: 'couvertsSoir', label: 'Couverts Soir' },
              { key: 'coutSalaire', label: 'Coût salaire' },
              { key: 'masseSalariale', label: 'Masse salariale' },
            ];
            const data = monthlyData[`${year}-${selectedMonth}`] || [];
            const nbJours = data.filter((d: any) => d.caJour !== undefined && d.caJour !== '').length;
            return fields.map(f => {
              const total = data.reduce((sum: number, d: any) => sum + (Number(d[f.key]) || 0), 0);
              const moyenne = nbJours > 0 ? total / nbJours : 0;
              let extra = null;
              if (f.key === 'caJour') {
                // Moyenne couverts jour (€)
                const nbJoursJour = data.filter((d: any) => d.caJour !== undefined && d.caJour !== '' && (d.couvertsMidi || d.couvertsSoir)).length;
                const moyCouvJour = nbJoursJour > 0 ? data.reduce((sum: number, d: any) => {
                  const couvertsJour = (Number(d.couvertsMidi) || 0) + (Number(d.couvertsSoir) || 0);
                  return sum + (couvertsJour ? (Number(d.caJour) / couvertsJour) : 0);
                }, 0) / nbJoursJour : 0;
                extra = <div style={{ color: theme.palette.primary.main, fontWeight: 500 }}>Moy. couv (€) : {moyCouvJour.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>;
              }
              if (f.key === 'caMidi') {
                const nbJoursMidi = data.filter((d: any) => d.caMidi !== undefined && d.caMidi !== '' && d.couvertsMidi).length;
                const moyCouvMidi = nbJoursMidi > 0 ? data.reduce((sum: number, d: any) => sum + ((d.caMidi && d.couvertsMidi) ? (Number(d.caMidi) / Number(d.couvertsMidi)) : 0), 0) / nbJoursMidi : 0;
                extra = <div style={{ color: theme.palette.primary.main, fontWeight: 500 }}>Moy. couv (€) : {moyCouvMidi.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>;
              }
              if (f.key === 'caSoir') {
                const nbJoursSoir = data.filter((d: any) => d.caSoir !== undefined && d.caSoir !== '' && d.couvertsSoir).length;
                const moyCouvSoir = nbJoursSoir > 0 ? data.reduce((sum: number, d: any) => sum + ((d.caSoir && d.couvertsSoir) ? (Number(d.caSoir) / Number(d.couvertsSoir)) : 0), 0) / nbJoursSoir : 0;
                extra = <div style={{ color: theme.palette.primary.main, fontWeight: 500 }}>Moy. couv (€) : {moyCouvSoir.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>;
              }
              let totalUnit = '';
              let moyenneUnit = '';
              if (["caMidi", "caSoir", "caJour", "coutSalaire", "masseSalariale"].includes(f.key)) {
                totalUnit = ' €';
                moyenneUnit = ' €';
              }
              return (
                <Paper key={f.key} elevation={4} sx={{background : "background.paper", color: "primary.main"}} style={{ minWidth: 150, borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{f.label}</div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Total : {['caMidi','caSoir','caJour','couvertsMidi','couvertsSoir','coutSalaire','masseSalariale'].includes(f.key) ? total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + totalUnit : '-'}</div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Moyenne : {moyenne.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + moyenneUnit}</div>
                  {extra && React.cloneElement(extra, { style: { ...extra.props.style, fontSize: 12 } })}
                </Paper>
              );
            });
          })()}
        </div>
        {/* Tableau des performances */}
        <div className='m-4'>
          {renderTable()}
        </div>

        {/* Popup de confirmation suppression */}
        <Dialog open={confirmDeleteDay !== null} onClose={() => setConfirmDeleteDay(null)} maxWidth="xs">
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 17, color: theme.palette.text.primary }}>Confirmer la suppression</div>
            <div>Cette action supprimera définitivement la performance du jour sélectionné. Voulez-vous continuer&nbsp;?</div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', color: theme.palette.text.primary}}>
              <Button variant="outlined" onClick={() => setConfirmDeleteDay(null)}>Annuler</Button>
              <Button variant="contained" color="error" onClick={confirmDelete}>Supprimer</Button>
            </div>
          </div>
        </Dialog>

        {/* Popup de saisie */}
        <Dialog open={popupDay !== null} onClose={() => setPopupDay(null)} maxWidth="xs">
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div><b>Saisie pour le {popupDay ? `${String(popupDay).padStart(2, '0')}/${String(selectedMonth+1).padStart(2, '0')}/${year}` : ''}</b></div>
            <TextField label="CA Midi" type="number" inputMode="decimal" value={popupValues.caMidi} onChange={e => handlePopupChange('caMidi', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSavePopup(); }} />
            <TextField label="Couverts Midi" type="number" inputMode="decimal" value={popupValues.couvertsMidi} onChange={e => handlePopupChange('couvertsMidi', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSavePopup(); }} />
            <TextField label="Couverts Soir" type="number" inputMode="decimal" value={popupValues.couvertsSoir} onChange={e => handlePopupChange('couvertsSoir', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSavePopup(); }} />
            <TextField label="CA Jour" type="number" inputMode="decimal" value={popupValues.caJour} onChange={e => handlePopupChange('caJour', e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSavePopup(); }} />
            <Button variant="contained" onClick={handleSavePopup}>Valider</Button>
          </div>
        </Dialog>
      </Paper>
  );
}

export default Performances;